import React, { memo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { HexColorPicker } from 'react-colorful';

import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  background: 'var(--color-surface)',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  outline: 'none',
  height: '40px',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  height: '40px',
  outline: 'none',
  paddingLeft: '8px',
  '& input': {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  '& input::placeholder': {
    color: 'var(--color-text-muted)',
    opacity: 1,
  },
}));

const ColorPreview = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
}));

const ColorInput = (props) => {
  const {
    value = '',
    onChange = () => {},
    label = '',
    required,
    disabled = false,
    sx,
  } = props;

  const [open, setOpen] = useState(false);

  return (
    <>
      {!!label && (
        <Box sx={{ display: 'flex', marginBottom: '2px' }}>
          <Typography sx={{ fontSize: '14px' }}>{label}</Typography>
          {required && (
            <Typography sx={(theme) => ({ fontSize: '16px', ml: '1px', color: theme.palette.primary.main })}>
              *
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
        <StyledFormControl sx={sx}>
          <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
            <StyledInput
              id={`color-input-${uuid()}`}
              value={value}
              placeholder="#000000"
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            />

            <ColorPreview
              sx={{ backgroundColor: value || '#ffffff' }}
              onClick={() => !disabled && setOpen((v) => !v)}
            />
          </Box>
        </StyledFormControl>

        {open && !disabled && (
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box
              sx={(theme) => ({
                position: 'absolute',
                zIndex: 10,
                mt: 1,
                p: 1,
                background: 'var(--color-surface-elevated)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 12px 28px rgba(0,0,0,0.4)'
                  : '0 4px 12px rgba(0,0,0,0.08)',
              })}
            >
              <HexColorPicker
                color={value || '#000000'}
                onChange={onChange}
                style={{
                  width: '220px',
                  height: '180px',
                  background: 'transparent',
                }}
              />
              <Box
                sx={(theme) => ({
                  mt: 1,
                  height: 32,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  background: value || '#000000',
                  boxShadow: `inset 0 0 0 1px ${alpha('#ffffff', theme.palette.mode === 'dark' ? 0.08 : 0.4)}`,
                })}
              />
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    </>
  );
};

export default memo(ColorInput);
